import {
  PAYMENT_TERMS,
  PAYMENT_TERMS_LABELS,
  PAYMENT_TERMS_NOTES,
  dueDateFor,
  type PaymentTerms,
} from '@jobos/shared';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatBillingDate } from './billingDate';

/**
 * The payment-terms control, in ONE place because three dialogs show it: both
 * creation dialogs and the send dialog. Three copies of a dropdown whose
 * options are wording the client owns is three places to miss when they change
 * it.
 *
 * The sentence under the control is the point of the component, not decoration.
 * "Due in 30 Days" does not say that two of the three choices carry a late-fee
 * warning, and the person picking is choosing what a customer reads.
 */
export interface PaymentTermsFieldProps {
  id: string;
  value: PaymentTerms;
  onChange: (value: PaymentTerms) => void;
  disabled?: boolean;
  /**
   * The date terms are counted from, 'YYYY-MM-DD'. Supply it to show the
   * resulting due date; omit it on the creation dialogs, where the invoice date
   * is assigned by the server and a date computed here could differ from the
   * one the invoice ends up with.
   */
  invoiceDate?: string;
}

/**
 * What an invoice with no terms yet opens on.
 *
 * 'Due Upon Receipt' is the status quo it replaces — every invoice sent before
 * this control existed printed no terms at all, and a customer who was never
 * given a date was, in practice, being asked to pay now. Defaulting to a
 * deadline would silently grant credit on every invoice nobody thought about.
 */
export const DEFAULT_PAYMENT_TERMS: PaymentTerms = 'due_on_receipt';

export function PaymentTermsField({
  id,
  value,
  onChange,
  disabled,
  invoiceDate,
}: PaymentTermsFieldProps) {
  const dueDate = invoiceDate ? dueDateFor(invoiceDate, value) : null;

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">
        Payment terms
      </label>
      <Select
        value={value}
        disabled={disabled}
        onValueChange={(next) => onChange(next as PaymentTerms)}
      >
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PAYMENT_TERMS.map((term) => (
            <SelectItem key={term} value={term}>
              {PAYMENT_TERMS_LABELS[term]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-gray-500">
        {dueDate && <span className="text-gray-700">Due {formatBillingDate(dueDate)}. </span>}
        Printed on the invoice: {PAYMENT_TERMS_NOTES[value]}
      </p>
    </div>
  );
}


